import type { JwtVerifiedCredential } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../../client/core/getCore';
import type { DynamicClient } from '../../../../../client/types';
import { assertDefined } from '../../../../../utils/assertDefined';
import { getChainFromVerifiedCredentialChain } from '../../../../../utils/getChainFromVerifiedCredentialChain';
import { formatWalletProviderKey } from '../../../utils/formatWalletProviderKey';

type GetWalletProviderKeyFromVerifiedCredentialParams = {
  verifiedCredential: JwtVerifiedCredential;
};

export const getWalletProviderKeyFromVerifiedCredential = (
  { verifiedCredential }: GetWalletProviderKeyFromVerifiedCredentialParams,
  client: DynamicClient
) => {
  const core = getCore(client);

  const { walletProviderKeyMap } = core.state.get();

  const storedWalletProviderKey = walletProviderKeyMap[verifiedCredential.id];

  if (storedWalletProviderKey) {
    return { walletProviderKey: storedWalletProviderKey };
  }

  /**
   * We fallback to comprising the wallet provider key from walletName and walletProvider.
   *
   * Some wallet provider types (like Wallet Connect) also use a special suffix for their wallet provider
   * keys, so this won't be enough for them.
   * Therefore, for those specific wallet providers, the wallet account will remain without a wallet provider
   * and will require reconnection.
   * Read walletProvider.types.ts for more info.
   */
  assertDefined(
    verifiedCredential.walletName,
    `Failed to get wallet provider for verified credential with ID ${verifiedCredential.id}: missing walletName`
  );

  assertDefined(
    verifiedCredential.walletProvider,
    `Failed to get wallet provider for verified credential with ID ${verifiedCredential.id}: missing walletProvider`
  );

  assertDefined(
    verifiedCredential.chain,
    `Failed to get wallet provider for verified credential with ID ${verifiedCredential.id}: missing chain`
  );

  /**
   * Historically, walletNames did not include the chain name as a suffix.
   * This piece of logic simply ensures we normalize any legacy walletNames to conform.
   */
  const chain = getChainFromVerifiedCredentialChain(verifiedCredential.chain);

  const walletProviderKey = formatWalletProviderKey({
    chain,
    displayName: verifiedCredential.walletName,
    walletProviderType: verifiedCredential.walletProvider,
  });

  return { walletProviderKey };
};
